import type { SpellText } from '@/lib/spell-check/spell-engine'
import type { SpellIssue } from '../types/spell-issue'
import type { SpellOccurrence } from '../types/spell-occurrence'
import { mapRangeThroughEdit } from './map-range-through-edit'

const textKey = (shapeId: string, onPath: boolean) => `${shapeId}\u0000${onPath ? 1 : 0}`

function textsByKey(texts: readonly SpellText[]): Map<string, string> {
  return new Map(
    texts.flatMap((t) =>
      t.shapeId === undefined ? [] : [[textKey(t.shapeId, !!t.onPath), t.text]]
    )
  )
}

/** Whether the occurrence says where it is, so it can be followed through an edit. */
function isPlaced(
  occurrence: SpellOccurrence
): occurrence is SpellOccurrence & { shapeId: string; start: number; end: number } {
  return (
    occurrence.shapeId !== undefined &&
    occurrence.start !== undefined &&
    occurrence.end !== undefined
  )
}

/**
 * Follows the user's Ignore / Add / Change choices through an edit of the text, so each one stays
 * on the instance it was made on (SC-14). `before` is the text the choices' positions were read
 * from and `after` is the text now. A choice moves with the text around it and is forgotten when
 * the edit touched its word, when the word's frame is gone, or when the edit cannot be told apart
 * from another one (`mapRangeThroughEdit`), so deleting an ignored word and typing it again is a
 * new misspelling, and an edit nearby never ignores a different instance.
 *
 * Open instances carry no choice, so they are left out. An issue left with no choice is dropped,
 * unless it was ignored with Ignore All, which stays until the word is typed again. The same array
 * comes back when nothing changed.
 */
export function trackResolutionsThroughEdits(
  resolved: readonly SpellIssue[],
  before: readonly SpellText[],
  after: readonly SpellText[]
): readonly SpellIssue[] {
  const beforeTexts = textsByKey(before)
  const afterTexts = textsByKey(after)
  let changed = false

  const tracked = resolved.flatMap((issue) => {
    const kept = issue.occurrences.flatMap((occurrence): SpellOccurrence[] => {
      if (occurrence.status === 'open') return []

      // A choice that does not say where it is (a state not read from a scan) is kept as it is.
      if (!isPlaced(occurrence)) return [occurrence]

      const key = textKey(occurrence.shapeId, occurrence.onPath === true)
      const oldText = beforeTexts.get(key)
      const newText = afterTexts.get(key)
      const moved =
        oldText === undefined || newText === undefined
          ? null
          : mapRangeThroughEdit(oldText, newText, occurrence.start, occurrence.end)

      if (!moved) return []
      if (moved[0] === occurrence.start && moved[1] === occurrence.end) return [occurrence]

      return [{ ...occurrence, start: moved[0], end: moved[1] }]
    })

    const unchanged =
      kept.length === issue.occurrences.length && kept.every((o, i) => o === issue.occurrences[i])

    if (unchanged) return [issue]

    changed = true

    // Ignore All is about the word, not its instances, so it outlives them all.
    return kept.length > 0 || issue.ignoredAll ? [{ ...issue, occurrences: kept }] : []
  })

  return changed ? tracked : resolved
}
